/**
 * My CTA — the logged-in user's personal referral link (Settings → My CTA tab).
 *
 * Shows/mints the user's personal attribution CTA (get_or_create_my_personal_cta). The link is
 * a real, shareable member-signup link carrying the user's code; sign-up tracking + credit
 * reporting are a deferred phase (docs/PLAN_PERSONAL_CTA_ATTRIBUTION.md) — surfaced honestly
 * as "coming soon" so the UI doesn't imply tracking that isn't wired yet.
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOrCreateMyPersonalCta } from "../../services/personalCta.service";

const MyCtaSettings: React.FC = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["my-personal-cta"],
    queryFn: getOrCreateMyPersonalCta,
  });

  const [copied, setCopied] = useState(false);

  async function copyLink() {
    if (!data?.generated_url) {
      return;
    }
    try {
      await navigator.clipboard.writeText(data.generated_url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard unavailable (permissions / insecure context) — the input is selectable.
    }
  }

  return (
    <div className="max-w-2xl space-y-4">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">My CTA</h2>
        <p className="mt-1 text-sm text-gray-600">
          Your personal referral link. Once this launches, sharing it will
          credit you for U.S. LawShield sign-ups you drive outside of events.
        </p>
      </div>

      {error ? (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          Could not load your CTA. Please try again.
        </div>
      ) : isLoading || !data ? (
        <p className="text-sm text-gray-500">Loading your link…</p>
      ) : !data.generated_url ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
          Your referral link is currently disabled.
        </div>
      ) : (
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <label className="text-xs font-medium text-gray-500">
              Your referral link
            </label>
            <div className="mt-1 flex gap-2">
              <input
                readOnly
                value={data.generated_url}
                onFocus={(e) => e.currentTarget.select()}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
              />
              <button
                type="button"
                onClick={copyLink}
                className="whitespace-nowrap rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                {copied ? "Copied" : "Copy"}
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              Your personal code:{" "}
              <span className="font-mono font-medium text-gray-700">
                {data.code}
              </span>
            </p>
          </div>

          <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-amber-900">
                Not active yet — please don&apos;t share this link
              </p>
              <span className="inline-flex rounded-full bg-amber-200 px-2.5 py-0.5 text-xs font-semibold text-amber-900">
                Coming Soon
              </span>
            </div>
            <p className="mt-1 text-sm text-amber-800">
              Sign-up credit isn&apos;t being tracked yet, so links shared now
              will not be credited. We&apos;ll let you know when it&apos;s live.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCtaSettings;
